import {
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import type { EpcProjectMembershipRow, EpcProjectOrgUnit } from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import {
  departmentHeadForOrg,
  membershipDisplayName,
  orgUnitName,
} from '../utils/epcScheduleDepartmentAssignment';
import { parseTaskDay } from '../utils/epcScheduleDatesDisplay';
import { taskPercent } from '../utils/taskStatus';

export type ResponsibilityRow = { roleLabel: string; name: string | null };

/** Dept head → manager → engineer, resolved from epcOrgUnitId + assignedUserId + reportsToUserId walk (spec §4.2).
 *  Falls back to the first active manager / site engineer in the org unit when the task has no specific
 *  assignment — so the department's team structure is always visible even on unassigned tasks. */
export function buildResponsibilityChain(
  memberships: EpcProjectMembershipRow[],
  orgUnits: EpcProjectOrgUnit[],
  orgUnitId: string | undefined,
  assignedUserId: string | undefined
): ResponsibilityRow[] {
  const head = departmentHeadForOrg(memberships, orgUnitId);
  const deptName = orgUnitName(orgUnits, orgUnitId);
  const assignee = assignedUserId
    ? memberships.find((m) => m.userId === assignedUserId && !m.inactive)
    : undefined;

  // Primary: derive manager from the assignee chain
  let manager: EpcProjectMembershipRow | undefined =
    assignee?.role === 'manager'
      ? assignee
      : assignee?.role === 'site_engineer' && assignee.reportsToUserId
        ? memberships.find((m) => m.userId === assignee.reportsToUserId && !m.inactive)
        : undefined;

  // Fallback: first active manager in this org unit (shown when task has no assignment or is assigned to dept head)
  if (!manager && orgUnitId) {
    manager = memberships.find(
      (m) => m.role === 'manager' && m.orgUnitId === orgUnitId && !m.inactive,
    );
  }

  // Primary: engineer is the assignee when they are a site engineer
  let engineer: EpcProjectMembershipRow | undefined =
    assignee?.role === 'site_engineer' ? assignee : undefined;

  // Fallback: first active site engineer in this org unit (prefer one reporting to the resolved manager)
  if (!engineer && orgUnitId) {
    engineer = manager
      ? memberships.find(
          (m) =>
            m.role === 'site_engineer' &&
            m.orgUnitId === orgUnitId &&
            m.reportsToUserId === manager.userId &&
            !m.inactive,
        )
      : memberships.find(
          (m) => m.role === 'site_engineer' && m.orgUnitId === orgUnitId && !m.inactive,
        );
  }

  return [
    {
      roleLabel: deptName ? `Dept head · ${deptName}` : 'Dept head',
      name: head ? membershipDisplayName(memberships, head.userId) : null,
    },
    { roleLabel: 'Manager', name: manager ? membershipDisplayName(memberships, manager.userId) : null },
    {
      roleLabel: engineer && !assignee ? 'Site engineer · department' : 'Site engineer · assigned',
      name: engineer ? membershipDisplayName(memberships, engineer.userId) : null,
    },
  ];
}

function numLevel(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Nearest previous/next row at the same outline level inside the same parent branch. */
export function findWbsSiblings(
  tasks: EpcScheduleTaskRow[],
  index: number
): { prev: number | null; next: number | null } {
  const L = numLevel(tasks[index]?.outlineLevel);
  let prev: number | null = null;
  for (let j = index - 1; j >= 0; j -= 1) {
    const lj = numLevel(tasks[j]?.outlineLevel);
    if (lj < L) break;
    if (lj === L) {
      prev = j;
      break;
    }
  }
  let next: number | null = null;
  for (let j = index + 1; j < tasks.length; j += 1) {
    const lj = numLevel(tasks[j]?.outlineLevel);
    if (lj < L) break;
    if (lj === L) {
      next = j;
      break;
    }
  }
  return { prev, next };
}

export type TimelineBar = { leftPct: number; widthPct: number };

export type MiniTimelineModel = {
  monthTicks: string[];
  bar: TimelineBar & { progressPct: number };
  prevBar: TimelineBar | null;
  nextBar: TimelineBar | null;
  /** Null when today is outside the 5-month window. */
  todayPct: number | null;
};

/** 5-month window centred on the task midpoint; bars and the today line as % of the window. */
export function buildMiniTimeline(
  tasks: EpcScheduleTaskRow[],
  index: number,
  today: Date
): MiniTimelineModel | null {
  const t = tasks[index];
  if (!t) return null;
  const s = parseTaskDay(t.start);
  const f = parseTaskDay(t.finish);
  if (!s || !f) return null;
  const mid = new Date(s.getTime() + (f.getTime() - s.getTime()) / 2);
  const winStart = startOfMonth(subMonths(mid, 2));
  const winEnd = endOfMonth(addMonths(mid, 2));
  const total = Math.max(1, differenceInCalendarDays(winEnd, winStart));
  const pct = (d: Date) =>
    Math.max(0, Math.min(100, (differenceInCalendarDays(startOfDay(d), winStart) / total) * 100));
  const barFor = (i: number | null): TimelineBar | null => {
    if (i == null) return null;
    const bs = parseTaskDay(tasks[i]?.start);
    const bf = parseTaskDay(tasks[i]?.finish);
    if (!bs || !bf) return null;
    const left = pct(bs);
    return { leftPct: left, widthPct: Math.max(1, pct(bf) - left) };
  };
  const { prev, next } = findWbsSiblings(tasks, index);
  const own = barFor(index);
  if (!own) return null;
  const day = startOfDay(today);
  return {
    monthTicks: Array.from({ length: 5 }, (_, k) => format(addMonths(winStart, k), 'MMM yy')),
    bar: { ...own, progressPct: taskPercent(t) },
    prevBar: barFor(prev),
    nextBar: barFor(next),
    todayPct: day >= winStart && day <= winEnd ? pct(day) : null,
  };
}
